import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { pdf, Document, Page, Text, View, StyleSheet } from '@react-pdf/renderer';

const ReceiptDetails = () => {
  const apiUrl = process.env.REACT_APP_API_BASE_URL;

  // State for receipts and loading
  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // State for filters
  const [filters, setFilters] = useState({
    receiptNo: '',
    customerName: '',
    invoiceNo: '',
    dateFrom: '',
    dateTo: '',
    paymentMode: '', // '' for all payment modes
  });

  // State for filtered results
  const [filteredReceipts, setFilteredReceipts] = useState([]);

  // Fetch receipts on component mount
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch invoices to get receipts
        const invoicesResponse = await axios.get(`${apiUrl}/api/invoices`);
        const responseData = invoicesResponse.data;
        
        // Handle different API response structures
        let invoicesData = [];
        if (Array.isArray(responseData)) {
          invoicesData = responseData;
        } else if (responseData?.data) {
          if (Array.isArray(responseData.data)) {
            invoicesData = responseData.data;
          } else if (responseData.data?.data && Array.isArray(responseData.data.data)) {
            invoicesData = responseData.data.data;
          }
        }
        
        // Flatten receipts from invoices
        let flattenedReceipts = [];
        invoicesData.forEach((invoice) => {
          if (invoice.receipts && Array.isArray(invoice.receipts)) {
            invoice.receipts.forEach((receipt) => {
              flattenedReceipts.push({
                ...receipt,
                _invoice: invoice, // Store parent invoice data
                _invoiceNo: invoice.invoice_no || invoice.invoice_number || '',
                _customerName: invoice.customer?.name || '',
              });
            });
          }
        });
        
        setReceipts(flattenedReceipts);
        setFilteredReceipts(flattenedReceipts);
        setError(null);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load receipt data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [apiUrl]);

  // Apply filters whenever they change or receipts change
  useEffect(() => {
    applyFilters();
  }, [filters, receipts]);

  // Helper function to get customer name from parent invoice
  const getCustomerName = (receipt) => {
    return receipt._customerName || receipt.customer?.name || receipt.customer_name || '';
  };

  // Helper function to get receipt number
  const getReceiptNo = (receipt) => {
    return receipt.receipt_number || receipt.receipt_no || receipt.id?.toString() || '';
  };

  // Helper function to get invoice number from parent invoice
  const getInvoiceNo = (receipt) => {
    return receipt._invoiceNo || receipt.invoice?.invoice_number || receipt.invoice_number || receipt.invoice_no || '';
  };

  // Helper function to get receipt date
  const getReceiptDate = (receipt) => {
    return receipt.receipt_date || receipt.date || receipt.created_at || '';
  };

  // Helper function to get payment mode
  const getPaymentMode = (receipt) => {
    return receipt.payment_mode || receipt.payment_method || receipt.mode || '';
  };

  // Apply filters to receipts
  const applyFilters = () => {
    let filtered = [...receipts];

    // Filter by receipt number
    if (filters.receiptNo) {
      filtered = filtered.filter((receipt) =>
        getReceiptNo(receipt)
          .toLowerCase()
          .includes(filters.receiptNo.toLowerCase())
      );
    }

    // Filter by customer name
    if (filters.customerName) {
      filtered = filtered.filter((receipt) =>
        getCustomerName(receipt)
          .toLowerCase()
          .includes(filters.customerName.toLowerCase())
      );
    }

    // Filter by invoice number
    if (filters.invoiceNo) {
      filtered = filtered.filter((receipt) =>
        getInvoiceNo(receipt)
          .toLowerCase()
          .includes(filters.invoiceNo.toLowerCase())
      );
    }

    // Filter by date range
    if (filters.dateFrom) {
      filtered = filtered.filter((receipt) => {
        const receiptDate = new Date(getReceiptDate(receipt));
        const fromDate = new Date(filters.dateFrom);
        return receiptDate >= fromDate;
      });
    }

    if (filters.dateTo) {
      filtered = filtered.filter((receipt) => {
        const receiptDate = new Date(getReceiptDate(receipt));
        const toDate = new Date(filters.dateTo);
        toDate.setHours(23, 59, 59, 999);
        return receiptDate <= toDate;
      });
    }

    // Filter by payment mode
    if (filters.paymentMode) {
      filtered = filtered.filter((receipt) =>
        getPaymentMode(receipt)
          .toLowerCase()
          .includes(filters.paymentMode.toLowerCase())
      );
    }

    setFilteredReceipts(filtered);
  };

  // Handle filter input change
  const handleFilterChange = (field, value) => {
    setFilters({
      ...filters,
      [field]: value,
    });
  };

  // Reset all filters
  const resetFilters = () => {
    setFilters({
      receiptNo: '',
      customerName: '',
      invoiceNo: '',
      dateFrom: '',
      dateTo: '',
      paymentMode: '',
    });
  };

  // Export table to PDF
  const exportToPDF = async () => {
    try {
      const pdfDocument = React.createElement(ReceiptDetailsPDFDocument);
      const blob = await pdf(pdfDocument).toBlob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `ReceiptDetails_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('PDF export failed:', err);
      alert('Unable to export PDF. Please try again.');
    }
  };

  // PDF Styles
  const pdfStyles = StyleSheet.create({
    page: {
      padding: 30,
      fontFamily: 'Helvetica',
    },
    title: {
      fontSize: 18,
      fontWeight: 'bold',
      marginBottom: 15,
      color: '#0891b2',
    },
    subtitle: {
      fontSize: 10,
      color: '#666',
      marginBottom: 20,
    },
    table: {
      width: '100%',
      marginBottom: 20,
    },
    tableHeader: {
      flexDirection: 'row',
      backgroundColor: '#f3f4f6',
      fontWeight: 'bold',
      fontSize: 9,
      borderBottomWidth: 1,
      borderBottomColor: '#000',
      paddingVertical: 6,
    },
    tableRow: {
      flexDirection: 'row',
      fontSize: 8,
      borderBottomWidth: 1,
      borderBottomColor: '#e5e7eb',
      paddingVertical: 6,
    },
    col1: {
      width: '12%',
      paddingHorizontal: 4,
      overflow: 'hidden',
    },
    col2: {
      width: '16%',
      paddingHorizontal: 4,
      overflow: 'hidden',
    },
    col3: {
      width: '16%',
      paddingHorizontal: 4,
      overflow: 'hidden',
    },
    col4: {
      width: '12%',
      paddingHorizontal: 4,
      overflow: 'hidden',
    },
    col5: {
      width: '16%',
      paddingHorizontal: 4,
      overflow: 'hidden',
    },
    col6: {
      width: '12%',
      paddingHorizontal: 4,
      overflow: 'hidden',
    },
    footerText: {
      fontSize: 9,
      color: '#666',
      marginTop: 10,
    },
  });

  // PDF Document Component
  const ReceiptDetailsPDFDocument = () => {
    const hasDateFilter = filters.dateFrom || filters.dateTo;
    const dateRangeText = hasDateFilter
      ? `${filters.dateFrom ? formatDate(filters.dateFrom) : 'Start'} - ${filters.dateTo ? formatDate(filters.dateTo) : 'End'}`
      : '';

    return (
      <Document>
        <Page size="A4" orientation="landscape" style={pdfStyles.page}>
          <Text style={pdfStyles.title}>Receipt Details Report</Text>
          <View style={{ marginBottom: 20 }}>
            <Text style={pdfStyles.subtitle}>
              Generated on {new Date().toLocaleDateString('en-GB', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
              })}
            </Text>
            {hasDateFilter && (
              <Text style={{ fontSize: 10, color: '#0891b2', fontWeight: 'bold', marginTop: 5 }}>
                Period: {dateRangeText}
              </Text>
            )}
          </View>

          <View style={pdfStyles.table}>
            {/* Table Header */}
            <View style={pdfStyles.tableHeader}>
              <Text style={pdfStyles.col1}>Receipt #</Text>
              <Text style={pdfStyles.col2}>Customer</Text>
              <Text style={pdfStyles.col3}>Invoice #</Text>
              <Text style={pdfStyles.col4}>Receipt Date</Text>
              <Text style={pdfStyles.col5}>Payment Mode</Text>
              <Text style={pdfStyles.col6}>Amount</Text>
            </View>

            {/* Table Rows */}
            {filteredReceipts.map((receipt) => (
              <View key={receipt.id} style={pdfStyles.tableRow}>
                <Text style={pdfStyles.col1}>{getReceiptNo(receipt)}</Text>
                <Text style={pdfStyles.col2}>{getCustomerName(receipt)}</Text>
                <Text style={pdfStyles.col3}>{getInvoiceNo(receipt)}</Text>
                <Text style={pdfStyles.col4}>{formatDate(getReceiptDate(receipt))}</Text>
                <Text style={pdfStyles.col5}>{getPaymentMode(receipt)}</Text>
                <Text style={pdfStyles.col6}>{formatCurrency(receipt.amount || receipt.total_amount || 0)}</Text>
              </View>
            ))}
          </View>

          <Text style={pdfStyles.footerText}>
            Showing {filteredReceipts.length} of {receipts.length} receipts
          </Text>
        </Page>
      </Document>
    );
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  // Format currency for display
  const formatCurrency = (amount) => {
    if (!amount) return 'AED 0.00';
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'AED',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg text-gray-600">Loading receipt data...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Receipt Details</h1>
          <p className="text-gray-600 mt-2">
            View and search all receipts
          </p>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
            {error}
          </div>
        )}

        {/* Filter Section */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Filters</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4 mb-4">
            {/* Receipt No Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Receipt #
              </label>
              <input
                type="text"
                placeholder="Search receipt..."
                value={filters.receiptNo}
                onChange={(e) =>
                  handleFilterChange('receiptNo', e.target.value)
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Customer Name Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Customer
              </label>
              <input
                type="text"
                placeholder="Search customer..."
                value={filters.customerName}
                onChange={(e) =>
                  handleFilterChange('customerName', e.target.value)
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Invoice No Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Invoice #
              </label>
              <input
                type="text"
                placeholder="Search invoice..."
                value={filters.invoiceNo}
                onChange={(e) =>
                  handleFilterChange('invoiceNo', e.target.value)
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Date From Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Date From
              </label>
              <input
                type="date"
                value={filters.dateFrom}
                onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Date To Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Date To
              </label>
              <input
                type="date"
                value={filters.dateTo}
                onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Payment Mode Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Payment Mode
              </label>
              <input
                type="text"
                placeholder="Search mode..."
                value={filters.paymentMode}
                onChange={(e) =>
                  handleFilterChange('paymentMode', e.target.value)
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          {/* Reset Button */}
          <button
            onClick={resetFilters}
            className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 transition-colors"
          >
            Reset Filters
          </button>

          {/* Export Button */}
          <button
            onClick={exportToPDF}
            disabled={filteredReceipts.length === 0}
            className="ml-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            Export to PDF
          </button>

          {/* Results Count */}
          <div className="mt-4 text-sm text-gray-600">
            Showing {filteredReceipts.length} of {receipts.length} receipts
          </div>
        </div>

        {/* Results Table */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          {filteredReceipts.length > 0 ? (
            <>
              <table className="w-full">
                <thead className="bg-gray-100 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                      Receipt #
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                      Customer
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                      Invoice #
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                      Receipt Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                      Payment Mode
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                      Amount
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredReceipts.map((receipt) => (
                    <tr
                      key={receipt.id}
                      className="hover:bg-gray-50 transition-colors"
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="font-medium text-blue-600">
                          {getReceiptNo(receipt)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getCustomerName(receipt)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {getInvoiceNo(receipt)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {formatDate(getReceiptDate(receipt))}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                          {getPaymentMode(receipt)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {formatCurrency(receipt.amount || receipt.total_amount || 0)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          ) : (
            <div className="p-8 text-center text-gray-500">
              No receipts found matching your filters.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReceiptDetails;
